// Small DOM helpers the page shell and a city's own UI share.

import { CASING, isDark } from '@tol/core/render';

export function el(selector: string, root: Document | Element = document): HTMLElement {
  const found = root.querySelector(selector);
  if (!(found instanceof HTMLElement)) throw new TypeError(`missing element ${selector}`);
  return found;
}

const ESCAPES: ReadonlyMap<string, string> = new Map([
  ['&', '&amp;'],
  ['<', '&lt;'],
  ['>', '&gt;'],
  ['"', '&quot;'],
  ["'", '&#39;'],
]);
export const esc = (s: string): string => s.replaceAll(/[&<>"']/gu, (c) => ESCAPES.get(c) ?? c);

// A line's color as CSS custom properties: the color, and a ring and edge
// that keep a dark line visible on the dark page.
export function lineVars(color: string): string {
  const dark = isDark(color);
  return `--c:${color};--ring:${dark ? CASING : 'transparent'};--edge:${dark ? CASING : color}`;
}
export function setLineColor(e: HTMLElement, color: string): void {
  const dark = isDark(color);
  e.style.setProperty('--c', color);
  e.style.setProperty('--ring', dark ? CASING : 'transparent');
  e.style.setProperty('--edge', dark ? CASING : color);
}

// A list refreshed while it's open (a card's, every second): each row is
// written only when its markup changed, and rows are kept, moved, added or
// removed by key, so an unchanged row keeps its node (its selection, and a
// screen reader's quiet) and a changed one is replaced alone. A key seen
// twice (a loop's station) counts its occurrences. The first write, and one
// after the list was written some other way, is the whole list.
export interface KeyedRow {
  readonly key: string;
  readonly html: string;
}
const written = new WeakMap<Element, { readonly keys: readonly string[]; readonly html: readonly string[] }>();
function fromHtml(html: string): Element | null {
  const t = document.createElement('template');
  t.innerHTML = html;
  return t.content.firstElementChild;
}
export function patchRows(list: HTMLElement, rows: readonly KeyedRow[]): void {
  const seen = new Map<string, number>();
  const keys = rows.map((r) => {
    const n = seen.get(r.key) ?? 0;
    seen.set(r.key, n + 1);
    return `${r.key}\u0000${String(n)}`;
  });
  const html = rows.map((r) => r.html);
  const last = written.get(list);
  written.set(list, { keys, html });
  if (!last || list.children.length !== last.keys.length) {
    list.innerHTML = html.join('');
    return;
  }
  const old = new Map<string, { readonly node: Element; readonly html: string }>();
  for (const [i, key] of last.keys.entries()) {
    const node = list.children[i];
    if (node) old.set(key, { node, html: last.html[i] ?? '' });
  }
  let at: Element | null = list.firstElementChild;
  for (const [i, key] of keys.entries()) {
    const want = html[i] ?? '';
    const had = old.get(key);
    old.delete(key);
    const node = had?.html === want ? had.node : fromHtml(want);
    if (!node) continue;
    if (had && node !== had.node) {
      if (at === had.node) at = had.node.nextElementSibling;
      had.node.replaceWith(node);
    }
    // In place already: the next row goes after it; else it's moved here.
    if (node === at) at = node.nextElementSibling;
    else list.insertBefore(node, at);
  }
  for (const { node } of old.values()) node.remove();
}

// A node's markup, written only when it changed since this wrote it.
const markup = new WeakMap<Element, string>();
export function setHtml(node: HTMLElement, html: string): void {
  if (markup.get(node) === html) return;
  node.innerHTML = html;
  markup.set(node, html);
}
